/**
 * LessonView - Main component for displaying and interacting with a lesson.
 * Supports progressive learning with reinforcement options.
 */

import React, { useState } from 'react';
import {
  BookOpen,
  Lightbulb,
  CheckCircle2,
  XCircle,
  RefreshCw,
  HelpCircle,
  Zap,
  ChevronRight,
  Clock,
} from 'lucide-react';
import type { Lesson } from '../../services/learningApi';

interface LessonViewProps {
  lesson: Lesson;
  onComplete: (score: number) => void;
  onRequestMoreExercises: () => void;
  onRequestExplanation: (errors: string[]) => void;
  onStartChallenge: () => void;
}

type LessonStage = 'intro' | 'rule' | 'tips' | 'examples' | 'exercises' | 'result';

const LessonView: React.FC<LessonViewProps> = ({
  lesson,
  onComplete,
  onRequestMoreExercises,
  onRequestExplanation,
  onStartChallenge,
}) => {
  const [stage, setStage] = useState<LessonStage>('intro');
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);

  const content = lesson.content;
  const exercises = lesson.exercises;

  // =========================================================================
  // STAGE NAVIGATION
  // =========================================================================

  const nextStage = () => {
    const stages: LessonStage[] = ['intro', 'rule', 'tips', 'examples', 'exercises', 'result'];
    const currentIndex = stages.indexOf(stage);
    if (currentIndex < stages.length - 1) {
      setStage(stages[currentIndex + 1]);
    }
  };

  // =========================================================================
  // EXERCISE HANDLING
  // =========================================================================

  const handleAnswer = (exerciseId: string, answer: any) => {
    setAnswers({ ...answers, [exerciseId]: answer });
  };

  const submitExercise = () => {
    const exercise = exercises[currentExerciseIndex];
    const userAnswer = answers[exercise.id];
    const isCorrect = userAnswer === exercise.correct_answer;

    if (!isCorrect) {
      setErrors([...errors, exercise.content?.sentence || exercise.instruction]);
    }

    setShowResult(true);

    // Auto-advance after 2 seconds
    setTimeout(() => {
      setShowResult(false);
      if (currentExerciseIndex < exercises.length - 1) {
        setCurrentExerciseIndex(currentExerciseIndex + 1);
      } else {
        // Calculate final score
        const correctCount = exercises.filter(
          (ex: any) => answers[ex.id] === ex.correct_answer
        ).length;
        const finalScore = Math.round((correctCount / exercises.length) * 100);
        setScore(finalScore);
        setStage('result');
      }
    }, 2000);
  };

  // =========================================================================
  // RENDER STAGES
  // =========================================================================

  const renderIntro = () => (
    <div className="text-center py-8">
      <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
        <BookOpen className="w-10 h-10 text-blue-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Leçon {lesson.number}: {lesson.title}
      </h2>
      {lesson.subtitle && (
        <p className="text-gray-500 mb-6">{lesson.subtitle}</p>
      )}
      <div className="bg-blue-50 rounded-lg p-6 mb-6 text-left max-w-xl mx-auto">
        <p className="text-gray-700">{content?.introduction}</p>
      </div>
      <div className="flex items-center justify-center gap-4 text-sm text-gray-500 mb-6">
        <span className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          ~{lesson.duration_minutes} min
        </span>
        <span className="flex items-center gap-1">
          <CheckCircle2 className="w-4 h-4" />
          {exercises.length} exercices
        </span>
      </div>
      <button
        onClick={nextStage}
        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
      >
        Commencer
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );

  const renderRule = () => (
    <div className="py-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
          <BookOpen className="w-4 h-4 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold">📌 Règle à retenir</h3>
      </div>

      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 mb-6">
        <h4 className="text-sm font-semibold text-green-700 mb-2">
          {content?.rule?.title}
        </h4>
        <p className="text-lg font-medium text-gray-800 mb-4">
          {content?.rule?.rule}
        </p>

        {content?.rule?.method && (
          <div className="mt-4 p-4 bg-white/50 rounded border border-green-200">
            <p className="text-sm font-medium text-green-800 mb-2">📝 Méthode:</p>
            <div className="text-sm text-gray-700 whitespace-pre-line">
              {content.rule.method}
            </div>
          </div>
        )}
      </div>

      <button
        onClick={nextStage}
        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
      >
        J'ai compris
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );

  const renderTips = () => (
    <div className="py-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
          <Lightbulb className="w-4 h-4 text-yellow-600" />
        </div>
        <h3 className="text-lg font-semibold">💡 Astuces pour mémoriser</h3>
      </div>

      <div className="space-y-4 mb-6">
        {content?.tips?.map((tip: { tip: string; example?: string }, idx: number) => (
          <div
            key={idx}
            className="bg-yellow-50 rounded-lg p-4 border-l-4 border-yellow-400"
          >
            <p className="font-medium text-gray-800 mb-2">{tip.tip}</p>
            {tip.example && (
              <p className="text-sm text-gray-600 italic">
                Exemple: {tip.example}
              </p>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={nextStage}
        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
      >
        Voir les exemples
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );

  const renderExamples = () => (
    <div className="py-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
          <CheckCircle2 className="w-4 h-4 text-purple-600" />
        </div>
        <h3 className="text-lg font-semibold">✨ Exemples illustrés</h3>
      </div>

      <div className="space-y-4 mb-6">
        {content?.examples?.map((example: { sentence: string; explanation: string; is_correct: boolean }, idx: number) => (
          <div
            key={idx}
            className={`rounded-lg p-4 ${
              example.is_correct
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <div className="flex items-start gap-3">
              {example.is_correct ? (
                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
              )}
              <div>
                <p className="font-medium text-gray-800 mb-1">
                  {example.sentence}
                </p>
                <p className="text-sm text-gray-600">{example.explanation}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={nextStage}
        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
      >
        Passer aux exercices
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );

  const renderExercises = () => {
    const exercise = exercises[currentExerciseIndex];
    const userAnswer = answers[exercise.id];

    return (
      <div className="py-6">
        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>Question {currentExerciseIndex + 1} / {exercises.length}</span>
            <span>{exercise.points} pts</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full">
            <div
              className="h-full bg-blue-600 rounded-full transition-all"
              style={{
                width: `${((currentExerciseIndex + 1) / exercises.length) * 100}%`,
              }}
            />
          </div>
        </div>

        {/* Exercise card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <p className="text-sm text-gray-500 mb-2">{exercise.instruction}</p>

          {/* Show the sentence/question for all exercise types */}
          {exercise.content?.sentence && (
            <p className="text-lg font-medium mb-4 p-3 bg-gray-50 rounded-lg">
              {exercise.content.sentence.replace(/_+/g, ' ______ ')}
            </p>
          )}

          {/* Fill blank exercise */}
          {exercise.type === 'fill_blank' && (
            <div>
              <div className="flex gap-3 flex-wrap">
                {exercise.content?.options?.map((option: string) => (
                  <button
                    key={option}
                    onClick={() => handleAnswer(exercise.id, option)}
                    disabled={showResult}
                    className={`px-6 py-3 rounded-lg border-2 transition ${
                      userAnswer === option
                        ? showResult
                          ? option === exercise.correct_answer
                            ? 'border-green-500 bg-green-50'
                            : 'border-red-500 bg-red-50'
                          : 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Multiple choice exercise */}
          {exercise.type === 'multiple_choice' && (
            <div className="space-y-3">
              {exercise.content?.options?.map((option: any, idx: number) => {
                const optionValue = Array.isArray(option) ? option.join(' / ') : option;
                return (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(exercise.id, optionValue)}
                    disabled={showResult}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition ${
                      userAnswer === optionValue
                        ? showResult
                          ? optionValue === (Array.isArray(exercise.correct_answer) ? exercise.correct_answer.join(' / ') : exercise.correct_answer)
                            ? 'border-green-500 bg-green-50'
                            : 'border-red-500 bg-red-50'
                          : 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {optionValue}
                  </button>
                );
              })}
            </div>
          )}

          {/* True/False exercise */}
          {exercise.type === 'true_false' && (
            <div>
              <div className="flex gap-3">
                {['Vrai', 'Faux'].map((option) => (
                  <button
                    key={option}
                    onClick={() => handleAnswer(exercise.id, option === 'Vrai')}
                    disabled={showResult}
                    className={`flex-1 px-6 py-3 rounded-lg border-2 transition ${
                      userAnswer === (option === 'Vrai')
                        ? showResult
                          ? (option === 'Vrai') === exercise.correct_answer
                            ? 'border-green-500 bg-green-50'
                            : 'border-red-500 bg-red-50'
                          : 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Correction exercise */}
          {exercise.type === 'correction' && (
            <div>
              <textarea
                placeholder="Écris la phrase corrigée ici..."
                className="w-full p-3 border border-gray-300 rounded-lg resize-none"
                rows={3}
                disabled={showResult}
                onChange={(e) => handleAnswer(exercise.id, e.target.value)}
              />
            </div>
          )}

          {/* Hint */}
          {exercise.hint && !showResult && (
            <p className="mt-4 text-sm text-gray-500 italic">
              💡 Indice: {exercise.hint}
            </p>
          )}

          {/* Result feedback */}
          {showResult && (
            <div
              className={`mt-4 p-4 rounded-lg ${
                userAnswer === exercise.correct_answer
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}
            >
              <p
                className={`font-medium ${
                  userAnswer === exercise.correct_answer
                    ? 'text-green-700'
                    : 'text-red-700'
                }`}
              >
                {userAnswer === exercise.correct_answer
                  ? '✅ Correct!'
                  : `❌ Incorrect. La bonne réponse est: ${exercise.correct_answer}`}
              </p>
              <p className="text-sm text-gray-600 mt-2">{exercise.explanation}</p>
            </div>
          )}
        </div>

        {/* Submit button */}
        {!showResult && userAnswer !== undefined && (
          <button
            onClick={submitExercise}
            className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Valider
          </button>
        )}
      </div>
    );
  };

  const renderResult = () => {
    const getScoreMessage = () => {
      if (score >= 90) return { emoji: '🏆', message: 'Excellent! Tu maîtrises cette notion!' };
      if (score >= 80) return { emoji: '⭐', message: 'Très bien! Tu y es presque!' };
      if (score >= 60) return { emoji: '💪', message: 'Pas mal! Continue à t\'entraîner!' };
      return { emoji: '📚', message: 'Ne te décourage pas! Révisons ensemble.' };
    };

    const { emoji, message } = getScoreMessage();

    return (
      <div className="py-8 text-center">
        <div className="text-6xl mb-4">{emoji}</div>
        <h2 className="text-2xl font-bold mb-2">Score: {score}%</h2>
        <p className="text-gray-600 mb-8">{message}</p>

        <div className="bg-gray-50 rounded-lg p-6 mb-8 max-w-md mx-auto">
          <h3 className="font-semibold mb-4">Que veux-tu faire?</h3>

          <div className="space-y-3">
            {score >= 80 && (
              <button
                onClick={() => onComplete(score)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                <CheckCircle2 className="w-5 h-5" />
                Passer à la suite
              </button>
            )}

            <button
              onClick={onRequestMoreExercises}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <RefreshCw className="w-5 h-5" />
              Plus d'exercices (+10)
            </button>

            {score < 80 && (
              <button
                onClick={() => onRequestExplanation(errors)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition"
              >
                <HelpCircle className="w-5 h-5" />
                Je n'ai pas compris
              </button>
            )}

            {score >= 70 && (
              <button
                onClick={onStartChallenge}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
              >
                <Zap className="w-5 h-5" />
                Défi chrono (20 questions)
              </button>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="text-left max-w-md mx-auto">
          <h4 className="font-semibold mb-3">📋 À retenir:</h4>
          <ul className="space-y-2">
            {content?.summary?.map((point: string, idx: number) => (
              <li key={idx} className="flex items-start gap-2 text-gray-700">
                <span className="text-green-500">✓</span>
                {point}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  // =========================================================================
  // MAIN RENDER
  // =========================================================================

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
        <h1 className="text-white font-semibold">
          {lesson.title}
        </h1>
        <p className="text-blue-100 text-sm">{lesson.subtitle}</p>
      </div>

      {/* Content */}
      <div className="p-6">
        {stage === 'intro' && renderIntro()}
        {stage === 'rule' && renderRule()}
        {stage === 'tips' && renderTips()}
        {stage === 'examples' && renderExamples()}
        {stage === 'exercises' && renderExercises()}
        {stage === 'result' && renderResult()}
      </div>
    </div>
  );
};

export default LessonView;
